// The details a subcontractor confirms at the signing gate — address, NI,
// licence and bank — and the rules they must pass.
//
// Mirrors subcontractor_confirm_contract_details() in SQL. The server is the
// authority and rejects anything that fails; this copy lets the form explain
// a problem before the round trip, with the same words. If these drift, fix
// the SQL first and copy here.

export const NI_RE = /^[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z][0-9]{6}[A-D]$/;
export const LICENCE_RE = /^[A-Z0-9]{5,20}$/;

export const CONTRACT_DETAIL_FIELDS = [
    { key: 'billing_address',    label: 'Home address',              kind: 'address' },
    { key: 'driver_license',     label: 'Driving licence number',    kind: 'text' },
    { key: 'national_insurance', label: 'National Insurance number', kind: 'text' },
    { key: 'bank_name',          label: 'Bank',                      kind: 'text' },
    { key: 'sort_code',          label: 'Sort code',                 kind: 'text' },
    { key: 'account_number',     label: 'Account number',            kind: 'text' },
];

export const digits = (v) => String(v ?? '').replace(/\D/g, '');
export const normaliseNi = (v) => String(v ?? '').toUpperCase().replace(/\s/g, '');
export const normaliseLicence = (v) => String(v ?? '').toUpperCase().replace(/\s/g, '');

/** 123456 / 12 34 56 / 12-34-56 -> 12-34-56 (partial input is left as digits). */
export function formatSortCode(v) {
    const d = digits(v).slice(0, 6);
    return d.length === 6 ? `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4, 6)}` : d;
}

/** Same messages, same order, as the SQL. */
export function validateContractDetails(f = {}) {
    const errors = {};
    if (String(f.billing_address ?? '').trim().length < 10) errors.billing_address = 'Enter your full postal address, including postcode.';
    if (!NI_RE.test(normaliseNi(f.national_insurance))) errors.national_insurance = 'National Insurance number should look like QQ123456C.';
    if (!LICENCE_RE.test(normaliseLicence(f.driver_license))) errors.driver_license = 'Driving licence number should be 5 to 20 letters and digits.';
    const bank = String(f.bank_name ?? '').trim();
    if (bank.length < 2 || bank.length > 60) errors.bank_name = 'Enter the name of your bank.';
    if (!/^[0-9]{6}$/.test(digits(f.sort_code))) errors.sort_code = 'Sort code should be 6 digits, e.g. 12-34-56.';
    if (!/^[0-9]{8}$/.test(digits(f.account_number))) errors.account_number = 'Account number should be 8 digits.';
    return { ok: Object.keys(errors).length === 0, errors };
}

/** Every field present on the profile (not necessarily valid, not necessarily confirmed). */
export function detailsPresent(profile) {
    return CONTRACT_DETAIL_FIELDS.every((f) => String(profile?.[f.key] ?? '').trim() !== '');
}

/** What the form should start with: the profile as it stands. */
export function detailsFromProfile(profile) {
    const out = {};
    for (const f of CONTRACT_DETAIL_FIELDS) out[f.key] = profile?.[f.key] ?? '';
    return out;
}
